// Failure-visibility guards for the in-browser ffmpeg.wasm repack pipeline.
//
// ffmpeg.wasm can leave a promise pending forever instead of rejecting: engine
// load can stall, and a wasm abort mid-exec can leave exec() unsettled (the
// single-threaded @ffmpeg/core's dispatcher worker swallows the thrown
// RuntimeError and never posts a reply). Without these guards the upload UI sits
// at 100% with no error. Each guard converts a hang into a thrown Error carrying
// the ffmpeg log tail. Kept in a standalone module so they're unit-testable
// without rendering the component (and so the component file stays component-only).

export const ENGINE_LOAD_TIMEOUT_MS = 60_000;   // first load also fetches the ~32 MB wasm
export const PROBE_EXEC_TIMEOUT_MS = 30_000;    // tiny input-parse pass; only guards a hang
export const DURATION_PROBE_TIMEOUT_MS = 20_000;
export const ENCODE_SILENCE_MS = 45_000;        // no log AND no progress for this long → stalled
export const ABORT_GRACE_MS = 10_000;           // after a bare Aborted(), exec must settle within this

export type FfmpegLogCb = (e: { message: string }) => void;
export type FfmpegProgressCb = (e: { progress: number }) => void;

// Minimal surface of @ffmpeg/ffmpeg's FFmpeg the watchdog needs.
export interface FfmpegRunner {
  on(event: 'log', cb: FfmpegLogCb): void;
  on(event: 'progress', cb: FfmpegProgressCb): void;
  off(event: 'log', cb: FfmpegLogCb): void;
  off(event: 'progress', cb: FfmpegProgressCb): void;
  // Resolves with ffmpeg's process exit code (0 = success).
  exec(args: string[]): Promise<number>;
}

// Reject if `p` doesn't settle within `ms`. Clears its timer on settle so a slow-
// but-successful op doesn't leave a dangling timer (or a late spurious rejection).
export function withTimeout<T>(p: Promise<T>, ms: number, message: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(message)), ms);
  });
  return Promise.race([p, timeout]).finally(() => clearTimeout(timer)) as Promise<T>;
}

// Run an ffmpeg exec() with a watchdog that turns silent hangs and hard wasm
// crashes into rejections, while NOT tripping on the benign trailing `Aborted()`.
//
// Why `Aborted()` is not, on its own, a failure: the single-threaded @ffmpeg/core
// prints a bare `Aborted()` to its log at the END of EVERY run (the emscripten
// runtime exits after main() returns; that's also why a second exec on the same
// instance fails). On a healthy encode exec() resolves with ret=0 in the same
// tick, so we only treat the abort as fatal if exec hasn't settled within
// ABORT_GRACE_MS of it. Genuine runtime crashes ("memory access out of bounds", a
// non-Aborted RuntimeError, OOM) never appear on a good run, so those reject
// immediately. The thrown message carries the ffmpeg log tail via `logTail()`.
export async function execWithWatchdog(
  ff: FfmpegRunner,
  args: string[],
  onProgress: (pct: number) => void,
  logTail: () => string,
): Promise<number> {
  let lastActivity = Date.now();
  let abortTimer: ReturnType<typeof setTimeout> | null = null;
  let silenceTimer: ReturnType<typeof setInterval> | null = null;
  let rejectWatch: ((e: Error) => void) | null = null;

  const watchdog = new Promise<never>((_, reject) => {
    rejectWatch = reject;
    silenceTimer = setInterval(() => {
      if (Date.now() - lastActivity > ENCODE_SILENCE_MS) {
        reject(new Error(
          `Audio transcode stalled (no activity for ${Math.round(ENCODE_SILENCE_MS / 1000)}s).\n` +
          `ffmpeg log:\n${logTail()}`,
        ));
      }
    }, 5_000);
  });

  const onLog: FfmpegLogCb = ({ message }) => {
    lastActivity = Date.now();
    if (/memory access out of bounds|RuntimeError|out of memory|\bOOM\b/i.test(message)) {
      rejectWatch?.(new Error(`Audio engine crashed during transcode.\nffmpeg log:\n${logTail()}`));
    } else if (/Aborted\(\)/i.test(message) && !abortTimer) {
      abortTimer = setTimeout(() => {
        rejectWatch?.(new Error(`Audio engine aborted and stopped responding.\nffmpeg log:\n${logTail()}`));
      }, ABORT_GRACE_MS);
    }
  };
  const onProg: FfmpegProgressCb = ({ progress }) => {
    lastActivity = Date.now();
    onProgress(Math.max(0, Math.min(100, Math.round(progress * 100))));
  };

  ff.on('log', onLog);
  try { ff.on('progress', onProg); } catch (_) { /* older ffmpeg.wasm builds */ }
  try {
    return await Promise.race([ff.exec(args), watchdog]);
  } finally {
    if (abortTimer) clearTimeout(abortTimer);
    if (silenceTimer) clearInterval(silenceTimer);
    ff.off('log', onLog);
    try { ff.off('progress', onProg); } catch (_) { /* ignore */ }
  }
}
